import { getSiloV3MarketEntry, siloV3LenderKey } from '@1delta/data-sdk'
import { createMarketUid } from '../../../utils'
import {
  fetchSiloMarketsLite,
  SILO_API_SUPPORTED_CHAIN_IDS,
  type SiloApiEntry,
} from '../../../lending/public-data/silo-v2/fetchPublic'
import type {
  OraclePriceEntry,
  ParseContext,
  TokenListMap,
  USDPriceMap,
} from '../types'

/**
 * Silo v3 GraphQL oracle-price prefetch. Mirrors `siloV2Graphql.ts` but
 * filters the shared `api-v3.silo.finance` endpoint to `protocolVersion:
 * 'v3'` and looks up registry entries via `getSiloV3MarketEntry`.
 *
 * Returns `null` when the indexer has no data for the chain so the caller
 * can fall back to the on-chain path.
 */
export async function fetchSiloV3GraphQLMarkets(
  chainId: string,
): Promise<SiloApiEntry[] | null> {
  if (!SILO_API_SUPPORTED_CHAIN_IDS.has(chainId)) return null
  try {
    const items = await fetchSiloMarketsLite(chainId, { protocolVersion: 'v3' })
    return items
  } catch (e) {
    console.warn(
      `[prices] Silo v3 GraphQL fetch failed on chain ${chainId}:`,
      (e as any)?.message ?? e,
    )
    return null
  }
}

/**
 * Morpho-style one-shot price fetcher for Silo v3. Same self-contained
 * resolution strategy as the v2 variant: parses with a USD-price map
 * derived from `supplyUsd / supply` ratios across the same response,
 * with the caller-provided `basePrices` overlaid on top.
 *
 * Mirrors `fetchMorphoGraphQLPrices` so callers can treat this as a
 * direct, parallel-runnable price source: returns parsed entries on
 * success or `null` to signal "fall back to on-chain".
 */
export async function fetchSiloV3GraphQLPrices(
  chainId: string,
  basePrices: USDPriceMap = {},
  tokenList: TokenListMap = {},
): Promise<OraclePriceEntry[] | null> {
  const items = await fetchSiloV3GraphQLMarkets(chainId)
  if (items == null) return null

  const usdPrices = buildSelfDerivedUsdMap(chainId, items, basePrices, tokenList)
  const context: ParseContext = { chainId, usdPrices, tokenList }
  const entries = parseSiloV3GraphQLResults(items, context)
  if (entries.length === 0) {
    // `items.length === 0` is the indexer simply having nothing for this chain
    // — a routine state, not a parse failure. Only warn when markets came back
    // and NONE of them yielded a price, which does mean the shaping broke.
    if (items.length > 0)
      console.warn(
        `[silo-v3-gql] chain ${chainId}: ${items.length} markets fetched but 0 price entries produced`,
      )
    return null
  }
  return entries
}

/**
 * Same self-derived USD-price logic as `siloV2Graphql.ts` —
 * `supplyUsd / supply` (or `borrowedUsd / borrowed`) per silo half,
 * overlaid with caller-provided `basePrices`. Kept inline rather than
 * shared because the v2/v3 fetchers intentionally don't import each
 * other's parsers.
 */
function buildSelfDerivedUsdMap(
  chainId: string,
  items: SiloApiEntry[],
  basePrices: USDPriceMap,
  tokenList: TokenListMap,
): USDPriceMap {
  const map: USDPriceMap = {}

  const set = (tokenLc: string, priceUSD: number) => {
    if (!Number.isFinite(priceUSD) || priceUSD === 0) return
    const groupKey =
      tokenList[tokenLc]?.assetGroup ?? `${chainId}-${tokenLc}`
    if (map[groupKey] == null) map[groupKey] = priceUSD
    if (map[tokenLc] == null) map[tokenLc] = priceUSD
  }

  for (const item of items) {
    for (const market of [item.market1, item.market2]) {
      if (!market) continue
      const tokenLc = (
        market.inputTokenId ??
        market.inputToken?.id ??
        ''
      ).toLowerCase()
      if (!tokenLc) continue

      const supply = safeNumber(market.supply)
      const supplyUsd =
        market.supplyUsd != null ? Number(market.supplyUsd) : 0
      if (supply > 0 && supplyUsd > 0) {
        set(tokenLc, supplyUsd / supply)
        continue
      }

      const borrowed = safeNumber(market.borrowed)
      const borrowedUsd =
        market.borrowedUsd != null ? Number(market.borrowedUsd) : 0
      if (borrowed > 0 && borrowedUsd > 0) {
        set(tokenLc, borrowedUsd / borrowed)
      }
    }
  }

  for (const [k, v] of Object.entries(basePrices)) {
    if (Number.isFinite(v) && v > 0) map[k] = v
  }

  return map
}

function safeNumber(v: string | number | null | undefined): number {
  if (v == null) return 0
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

function lookupUSD(context: ParseContext, asset: string): number {
  const lc = asset.toLowerCase()
  const groupKey =
    context.tokenList?.[lc]?.assetGroup ?? `${context.chainId}-${lc}`
  return context.usdPrices[groupKey] ?? context.usdPrices[lc] ?? 0
}

/**
 * Convert a GraphQL-fetched silos list into oracle price entries. Logic
 * identical to the v2 variant — resolves USD via the indexer's
 * `supplyUsd`/`supply` ratio first, falling back to `quote × quoteTokenUSD`
 * for empty silos.
 */
export function parseSiloV3GraphQLResults(
  items: SiloApiEntry[],
  context: ParseContext,
): OraclePriceEntry[] {
  const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'
  const out: OraclePriceEntry[] = []
  const seen = new Set<string>()

  for (const item of items) {
    let lenderKey: string
    try {
      lenderKey = siloV3LenderKey(item.configAddress)
    } catch {
      continue
    }

    const registryEntry = getSiloV3MarketEntry(context.chainId, lenderKey)
    if (!registryEntry) continue

    const [apiSide0, apiSide1] =
      item.market1.index === 0
        ? [item.market1, item.market2]
        : [item.market2, item.market1]

    const sideTuples = [
      { api: apiSide0, self: registryEntry.silo0, other: registryEntry.silo1 },
      { api: apiSide1, self: registryEntry.silo1, other: registryEntry.silo0 },
    ]

    for (const { api: side, self, other } of sideTuples) {
      const tokenLc = (
        side.inputTokenId ??
        side.inputToken?.id ??
        self.token
      ).toLowerCase()
      if (!tokenLc) continue

      const partnerTokenLc = other.token.toLowerCase()

      const dedupKey = `${lenderKey}:${tokenLc}`
      if (seen.has(dedupKey)) continue

      const oracleAddr = (
        side.solvencyOracleAddress ??
        side.solvencyOracle?.id ??
        self.solvencyOracle ??
        ZERO_ADDRESS
      ).toLowerCase()
      const isStatic = !oracleAddr || oracleAddr === ZERO_ADDRESS

      const siloAddrLc = self.silo.toLowerCase()

      try {
        let priceUSD = 0
        let rawPrice = 1

        const supplyNum = safeNumber(side.supply)
        const supplyUsdNum =
          side.supplyUsd != null ? Number(side.supplyUsd) : 0
        const borrowedNum = safeNumber(side.borrowed)
        const borrowedUsdNum =
          side.borrowedUsd != null ? Number(side.borrowedUsd) : 0

        if (supplyNum > 0 && supplyUsdNum > 0) {
          priceUSD = supplyUsdNum / supplyNum
          rawPrice = isStatic ? 1 : priceUSD
        } else if (borrowedNum > 0 && borrowedUsdNum > 0) {
          priceUSD = borrowedUsdNum / borrowedNum
          rawPrice = isStatic ? 1 : priceUSD
        } else if (isStatic) {
          const ownUSD = lookupUSD(context, tokenLc)
          const partnerUSD = partnerTokenLc
            ? lookupUSD(context, partnerTokenLc)
            : 0
          priceUSD = ownUSD || partnerUSD
          if (!priceUSD) continue
          rawPrice = 1
        } else {
          const quoteStr = side.solvencyOracle?.quote
          if (!quoteStr) continue
          const quoteNum = Number(quoteStr)
          if (!Number.isFinite(quoteNum) || quoteNum === 0) continue

          const quoteTokenLc = (
            side.solvencyOracle?.quoteTokenId ?? ''
          ).toLowerCase()
          if (!quoteTokenLc) continue

          const qtUSD = lookupUSD(context, quoteTokenLc)
          if (!qtUSD) continue

          rawPrice = quoteNum
          priceUSD = quoteNum * qtUSD
        }

        if (!Number.isFinite(priceUSD) || priceUSD === 0) continue

        out.push({
          asset: tokenLc,
          price: rawPrice,
          priceUSD,
          marketUid: createMarketUid(context.chainId, lenderKey, siloAddrLc),
          targetLender: lenderKey,
          ...(isStatic ? { staticBase: true, baseAsset: tokenLc } : {}),
        })
        seen.add(dedupKey)
      } catch {
        // skip bad entries
      }
    }
  }

  return out
}
